import type { ReactNode } from "react";
import type { TooltipProps } from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

export const CHART_TOOLTIP_STYLE = {
  background: "var(--color-popover)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  fontSize: 12,
  color: "var(--color-popover-foreground)",
  boxShadow: "0 4px 12px oklch(0 0 0 / 0.12)",
} as const;

type FormatterResult = ReactNode | [ReactNode, ReactNode];

/**
 * Recharts' default tooltip paints each row in its series colour and falls back to #000 when a bar is coloured
 * per Cell, which vanishes on the dark popover. Text stays in the popover foreground; the colour is a swatch.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
  separator = ": ",
}: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) return null;
  const heading = labelFormatter ? labelFormatter(label, payload) : label;

  return (
    <div style={CHART_TOOLTIP_STYLE} className="min-w-32 px-2.5 py-2">
      {heading !== undefined && heading !== "" && <p className="mb-1 font-medium">{heading}</p>}
      <ul className="space-y-0.5">
        {payload.map((entry, index) => {
          const formatted: FormatterResult = formatter
            ? (formatter(entry.value as ValueType, entry.name as NameType, entry, index, payload) as FormatterResult)
            : entry.value;
          const [value, name] = Array.isArray(formatted) ? formatted : [formatted, entry.name];
          const swatch = typeof entry.color === "string" && !entry.color.startsWith("url(") ? entry.color : undefined;
          return (
            <li key={`${String(entry.dataKey ?? entry.name)}-${index}`} className="flex items-center gap-1.5">
              {swatch && <span className="size-2 shrink-0 rounded-[2px]" style={{ background: swatch }} />}
              <span className="text-muted-foreground">
                {name}
                {separator}
              </span>
              <span className="ml-auto pl-3 font-medium tabular-nums">{value}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Spread onto every Recharts <Tooltip>: theme-aware content and a hover band that reads in both themes. */
export const chartTooltipProps = {
  content: <ChartTooltipContent />,
  cursor: { fill: "var(--color-accent)", opacity: 0.6 },
  wrapperStyle: { outline: "none" },
} as const;
